import type { Watchlist } from '@benzinga/watchlist-manager';

import {
  aggregateHoldingsBySymbol,
  priceHoldings,
  sumPricedHoldingValues,
} from '../../../profile/portfolio/_components/portfolioActionUtils';

type DelayedQuotePrice = { lastTradePrice?: number | null; symbol?: string | null };

export const pricesFromDelayedQuotes = (
  quotes: Record<string, DelayedQuotePrice | null | undefined> | null | undefined,
): Record<string, number> => {
  const pricesBySymbol: Record<string, number> = {};

  Object.entries(quotes ?? {}).forEach(([symbol, quote]) => {
    const lastTradePrice = quote?.lastTradePrice;
    if (typeof lastTradePrice !== 'number' || !Number.isFinite(lastTradePrice) || lastTradePrice <= 0) return;
    pricesBySymbol[symbol] = lastTradePrice;
    if (quote?.symbol) pricesBySymbol[quote.symbol] = lastTradePrice;
  });

  return pricesBySymbol;
};

export const delayedQuotesHavePrices = (
  quotes: { ok?: Record<string, DelayedQuotePrice | null | undefined> | null } | null | undefined,
): boolean => Object.keys(pricesFromDelayedQuotes(quotes?.ok)).length > 0;

export const fetchDelayedQuotesWithRetry = async <
  T extends { ok?: Record<string, DelayedQuotePrice | null | undefined> | null },
>(
  requestQuotes: () => Promise<T>,
  symbolCount: number,
): Promise<T | null> => {
  let quotes: T | null = await requestQuotes().catch(() => null);
  if (!delayedQuotesHavePrices(quotes) && symbolCount > 0) {
    quotes = await requestQuotes();
  }
  return quotes;
};

export const overviewHoldingStats = (
  watchlists: Pick<Watchlist, 'symbols'>[],
  pricesBySymbol: Record<string, number>,
) => {
  const holdingValues = sumPricedHoldingValues(priceHoldings(aggregateHoldingsBySymbol(watchlists), pricesBySymbol));
  const change = holdingValues.totalValue - holdingValues.costBasis;

  return {
    change,
    changePercent: holdingValues.costBasis > 0 ? (change / holdingValues.costBasis) * 100 : 0,
    cost: holdingValues.costBasis,
    total: holdingValues.totalValue,
  };
};
